import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { FormHelper } from '@app/ui-forms/classes/form-helper';
import { DomainHelper } from '@app/core/helpers/domain.helper';

import { ScriptLoaderService } from '@app/core/services/script-loader.service';
import { ConfigureService } from '@app/configure/services/configure.service';
import { TrackingService } from '@app/core/services/tracking.service';
import { SessionService } from '@app/core/services/session.service';
import { HelperService } from '@app/core/services/helper.service';
import { ToastService } from '@app/core/services/toast.service';

import { FormInputEvent } from '@app/ui-forms/models/form-input-event.model';
import { Dealer } from '@app/core/models/dealer.model';

import { environment } from '@environments/environment';

@Component({
	selector: 'cng-payment-sense',
	templateUrl: './payment-sense.component.html',
	styleUrls: ['./payment-sense.component.less'],
})
export class ConfigurePaymentSenseComponent implements OnInit, OnDestroy {
	@Input() key: string = '';
	@Input() test_mode: boolean = false;
	@Input() payment_type: 'buyNow' | 'reserve' = 'reserve';

	payment_sense_form: FormGroup;
	connectE: any = null;
	payButton: any = null;
	card: any = null;
	enquiry_hash: string = this.configure_service.configure_data.meta.enquiry_hash;
	customer: any = this.configure_service.configure_data.meta.customer;
	payment: any = this.helper_service.copy(this.configure_service.configure_data.steps.payment.data);
	vehicle: any = this.configure_service.configure_data.steps.purchase_vehicle.data;
	reservation_amount: number =
		this.configure_service.configure_data.steps.payment.data.reservation.reservation_amount;
	grand_total: number = this.configure_service.configure_data.steps.customise_review.data.grand_total;
	dealer: Dealer = this.session_service.getDealer();
	errors: any = [];
	payment_sense_form_touched: boolean = false;
	gte_sm: boolean = this.helper_service.isScreenWidth('>=', 'sm');

	paymentSenseConfig: any = {
		paymentDetails: {
			amount: this.reservation_amount * 100 + '', // convert the reservation amount to minor unit
			currencyCode: '826', // GBP
			paymentToken: null,
		},
		containerId: 'payment-sense-payment',
		fontCss: ['https://fonts.googleapis.com/css2?family=Roboto'],
		styles: this.getStyles(),
	};

	sending: boolean = false;
	has_card_details_errors: boolean = false;
	payment_sense_logo: string = `${environment.deploy_protocol}${environment.deploy_domain}/assets/img/payment-sense.png`;
	tracking_log: string[] = [];
	destroyed$: Subject<boolean> = new Subject<boolean>();

	constructor(
		private script_loader_service: ScriptLoaderService,
		private element_ref: ElementRef,
		private configure_service: ConfigureService,
		private helper_service: HelperService,
		private toast_service: ToastService,
		private tracking_service: TrackingService,
		private session_service: SessionService
	) {}

	ngOnInit(): void {
		this.buildForm();

		const payment_sense_version = this.test_mode ? 'payment_sense_sandbox' : 'payment_sense';
		const payment_sense_script = this.script_loader_service.load({ name: payment_sense_version });
		const get_token = this.configure_service.generatePaymentToken('payment-sense').toPromise();

		Promise.all([payment_sense_script, get_token]).then((response) => {
			this.initPaymentSense(response[1]);
		});

		this.configure_service.configure_data$.pipe(takeUntil(this.destroyed$)).subscribe((configure_data) => {
			this.enquiry_hash = configure_data.meta.enquiry_hash;
			this.customer = configure_data.meta.customer;
			this.vehicle = configure_data.steps.purchase_vehicle.data;
			this.payment = this.helper_service.copy(configure_data.steps.payment.data);
			this.buildForm();
		});
	}

	buildForm(): void {
		this.payment_sense_form = new FormGroup({
			first_name: new FormControl(this.customer.first_name, Validators.required),
			last_name: new FormControl(this.customer.last_name, Validators.required),
			email: new FormControl(
				this.customer.email === null ? this.customer.email : this.customer.email.toLowerCase(),
				[Validators.required, Validators.email]
			),
			tel_mob: new FormControl(this.customer.tel_mob, [Validators.required, Validators.pattern('[0-9 +]+')]),
			terms: new FormControl(false, [Validators.requiredTrue]),
		});
	}

	initPaymentSense(token: string): void {
		this.paymentSenseConfig.paymentDetails.paymentToken = token;

		this.connectE = new (<any>window).Connect.ConnectE(this.paymentSenseConfig, this.handleErrors.bind(this));

		this.payButton = this.element_ref.nativeElement.querySelector('#testPay');
	}

	handleErrors(errors): void {
		this.trackCardInput();

		this.payment_sense_form_touched = true;
		this.errors = errors;
		this.has_card_details_errors = this.errors.length > 0;
	}

	onSubmit(): void {
		if (this.sending) {
			return;
		}

		this.tracking_service.trackFormSubmitAttempt(
			'payment_sense_form',
			FormHelper.getCompletedControls(this.payment_sense_form),
			FormHelper.getEmptyControls(this.payment_sense_form)
		);
		if (this.connectE !== null && this.payment_sense_form_touched && this.payment_sense_form.valid) {
			this.sending = true;

			this.connectE
				.executePayment()
				.then((data: any) => {
					if (data.statusCode === 0) {
						this.tracking_service.trackFormValidationPass('payment_sense_form');
						this.createPayment(data);

						return;
					}

					this.showSoftError();

					this.configure_service
						.generatePaymentToken('payment-sense')
						.pipe(takeUntil(this.destroyed$))
						.subscribe((token: string) => {
							this.paymentSenseConfig.paymentDetails.paymentToken = token;

							this.connectE.updateAccessToken(this.paymentSenseConfig.paymentDetails);
						});

					this.sending = false;
				})
				.catch((error) => {
					console.log('Payment Request failed: ' + error.message);

					this.showSoftError();

					this.sending = false;
				});
		} else {
			FormHelper.forceValidate(this.payment_sense_form);
			this.payment_sense_form_touched = true;
			this.trackValidationFailure();
			this.toast_service.error('Form has invalid fields');
		}
	}

	showSoftError(): void {
		this.configure_service.setSoftError({
			title: 'Payment failed',
			message:
				"We're sorry, we were unable to take payment at this time using the details you provided. Please arrange a callback so we can discuss your reservation.",
		});
		this.tracking_service.trackFormSubmitFailure('payment_sense_form', 'payment_failed');
	}

	createPayment(data: any): void {
		const body = {
			payment: {
				service: 'payment-sense',
				id: this.paymentSenseConfig.paymentDetails.paymentToken,
			},
			customer: {
				first_name: this.payment_sense_form.controls['first_name'].value,
				last_name: this.payment_sense_form.controls['last_name'].value,
				email: this.payment_sense_form.controls['email'].value.toLowerCase().replace(/ /g, ''),
				tel_mob: this.payment_sense_form.controls['tel_mob'].value.toLowerCase().replace(/ /g, ''),
			},
		};

		this.configure_service.configure_data.meta.customer = body.customer;

		this.configure_service.createPaymentCharge(body, this.payment_type).subscribe((response: any) => {
			this.handlePaymentResponse(response);
		});
	}

	handlePaymentResponse(response) {
		if (response === true) {
			this.toast_service.success('Payment successful!');
			this.tracking_service.trackFormSubmitSuccess('payment_sense_form');
			if (this.payment_type === 'buyNow') {
				this.tracking_service.trackFullPayment(this.grand_total);
			} else {
				this.tracking_service.trackReservationPayment(this.reservation_amount);
			}
		} else {
			this.showSoftError();
		}
		this.sending = false;
	}

	onTermsCta(event: any): void {
		if (event === 'terms-and-conditions') {
			window.open(this.dealer.terms_and_conditions_url);
			return;
		}
		window.open(`//${window.location.host}${DomainHelper.getBaseHref()}configure/${event}`, '_blank');
	}

	getStyles(): any {
		return {
			base: {
				default: {
					color: '#111',
					textDecoration: 'none',
					fontFamily: '"Roboto", "Trebuchet", Arial, sans-serif',
					boxSizing: 'border-box',
					padding: '0 12px',
					boxShadow: 'none',
					fontSize: '16px',
					borderRadius: '3px',
					lineHeight: '38px',
					backgroundColor: '#fff',
					borderColor: '#ccc',
					height: '40px',
				},

				focus: {
					borderColor: '#ccc',
					outline: 'none',
				},
				error: {
					color: this.dealer.branding.negative_background,
					borderColor: this.dealer.branding.negative_background,
				},
				valid: {
					color: '#111',
					borderColor: '#ccc',
				},
				label: {
					display: 'none',
				},
			},
			cardNumber: this.gte_sm
				? {
						container: {
							width: '50%',
							float: 'left',
						},
						default: {
							borderRadius: '3px 0 0 3px',
							borderRight: 'none',
						},
				  }
				: {},
			expiryDate: this.gte_sm
				? {
						container: {
							width: '25%',
							float: 'left',
						},
						default: {
							borderRadius: '0',
							borderRight: 'none',
						},
				  }
				: {
						container: {
							width: '50%',
							float: 'left',
						},
						default: {
							borderRight: 'none',
							borderRadius: '3px 0 0 3px',
						},
				  },
			cv2: this.gte_sm
				? {
						container: {
							width: '25%',
							float: 'left',
						},
						default: {
							borderRadius: '0 3px 3px 0',
						},
				  }
				: {
						container: {
							width: '50%',
							float: 'left',
						},
						default: {
							borderRadius: '0 3px 3px 0',
						},
				  },
		};
	}

	onFormInput(form: any): void {
		const event: FormInputEvent = FormHelper.getInputEvent(form);
		if (this.tracking_log.indexOf(event.log_name) === -1) {
			this.tracking_service.trackFormInput(event.form_name, event.input_type, event.input_name);
			this.tracking_log.push(event.log_name);
		}
	}

	trackCardInput(): void {
		const log_name: string = `payment_sense_form__card_control__payment_sense`;
		if (this.tracking_log.indexOf(log_name) === -1) {
			this.tracking_service.trackFormInput('configure_payment_sense_form', 'card_control', 'payment_sense');
			this.tracking_log.push(log_name);
		}
	}

	trackValidationFailure(): void {
		const invalid_fields: string[] = FormHelper.getInvalidControls(this.payment_sense_form);
		if (this.has_card_details_errors) {
			invalid_fields.push('payment_sense');
		}
		this.tracking_service.trackFormValidationFailure(
			'payment_sense_form',
			'invalid_fields[' + invalid_fields.join(',') + ']'
		);
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
	}
}
